



import React, { useState } from 'react';
import html2pdf from 'html2pdf.js';
import CalculatorForm from './CalculatorForm';
import { motion } from 'framer-motion';
import {
  FaChartLine,
  FaPiggyBank,
  FaShieldAlt,
  FaFilePdf,
  FaWallet,
  FaBullseye,
} from 'react-icons/fa';

const RetirementCalculator = () => {
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [lifeExpectancy, setLifeExpectancy] = useState(85);
  const [currentSavings, setCurrentSavings] = useState(500000);
  const [monthlyInvestment, setMonthlyInvestment] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [monthlyExpense, setMonthlyExpense] = useState(50000);
  const [inflationRate, setInflationRate] = useState(6);

  const [showForm, setShowForm] = useState(false);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const yearsToRetirement = retirementAge - currentAge;

    if (yearsToRetirement <= 0) {
      alert('Retirement age must be greater than current age');
      return;
    }

    if (lifeExpectancy <= retirementAge) {
      alert('Life expectancy must be greater than retirement age');
      return;
    }

    const retirementYears = lifeExpectancy - retirementAge;
    const months = yearsToRetirement * 12;
    const monthlyRate = expectedReturn / 12 / 100;

    // Future Value of Existing Savings
    const futureValueOfSavings =
      currentSavings *
      Math.pow(1 + expectedReturn / 100, yearsToRetirement);

    // Future Value of SIP
    const futureValueOfSIP =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) /
        monthlyRate) *
      (1 + monthlyRate);

    // Total Corpus at Retirement
    const totalCorpus =
      futureValueOfSavings + futureValueOfSIP;

    // Inflation Adjusted Expense
    const inflationAdjustedExpense =
      monthlyExpense *
      Math.pow(
        1 + inflationRate / 100,
        yearsToRetirement
      );

    // Retirement Corpus Needed
    const requiredCorpus =
      inflationAdjustedExpense *
      12 *
      retirementYears;

    // Gap Analysis
    const retirementGap =
      requiredCorpus - totalCorpus;

    const isSufficient =
      totalCorpus >= requiredCorpus;

    // Additional SIP Needed
    let additionalMonthlyInvestment = 0;

    if (!isSufficient) {
      additionalMonthlyInvestment = Math.ceil(
        retirementGap /
        (((Math.pow(1 + monthlyRate, months) - 1) /
          monthlyRate) *
          (1 + monthlyRate) || 1)
      );
    }

    setResult({
      yearsToRetirement,
      retirementYears,
      totalCorpus,
      requiredCorpus,
      retirementGap,
      isSufficient,
      futureValueOfSavings,
      futureValueOfSIP,
      inflationAdjustedExpense,
      additionalMonthlyInvestment,
    });
  };

  const handleFormSubmit = (userData) => {
    const element = document.getElementById(
      'retirement-result-content'
    );

    const opt = {
      margin: [0.4, 0.4, 0.4, 0.4],
      filename: `Retirement_Report_${userData.name}.pdf`,
      image: {
        type: 'jpeg',
        quality: 1,
      },
      html2canvas: {
        scale: 2,
      },
      jsPDF: {
        unit: 'in',
        format: 'a4',
        orientation: 'portrait',
      },
    };

    html2pdf().set(opt).from(element).save();

    setShowForm(false);

    const whatsappMessage = `
Dear ${userData.name},

📊 Retirement Planning Summary

👤 Current Age: ${currentAge} Years
🏖 Retirement Age: ${retirementAge} Years
🎯 Life Expectancy: ${lifeExpectancy} Years

💰 Estimated Retirement Corpus:
₹${Math.round(
      result.totalCorpus
    ).toLocaleString()}

🎯 Required Retirement Corpus:
₹${Math.round(
      result.requiredCorpus
    ).toLocaleString()}

📈 Inflation Adjusted Monthly Expense:
₹${Math.round(
      result.inflationAdjustedExpense
    ).toLocaleString()}

${result.isSufficient
        ? '✅ Congratulations! Your retirement planning is on track.'
        : `⚠ Additional SIP Needed: ₹${result.additionalMonthlyInvestment.toLocaleString()} / month`
      }

For personalized investment guidance:

Wisdom Consultancy
📞 +91 9726344477
📧 wisdom2secure@gmail.com
🌐 www.wisdomconsultancy.in
`;

    window.open(
      `https://wa.me/${userData.phone}?text=${encodeURIComponent(
        whatsappMessage
      )}`,
      '_blank'
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-[35px] shadow-xl p-6 md:p-10 border border-[#edf3ff]"
    >
      {/* HEADER */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-3 bg-[#edf3ff] px-5 py-3 rounded-full mb-5">
          <div className="w-10 h-10 rounded-full bg-[#2e56b8] flex items-center justify-center text-white">
            <FaPiggyBank />
          </div>

          <span className="font-semibold text-[#0b1c39]">
            Smart Retirement Planning
          </span>
        </div>

        <h3 className="text-3xl md:text-4xl font-bold text-[#0b1c39] mb-3">
          Retirement Calculator
        </h3>

        <p className="text-gray-600 text-lg leading-8">
          Plan your retirement corpus with inflation-adjusted calculations and long-term financial projections.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* LEFT INPUTS */}
        <div className="space-y-6">
          {/* CURRENT AGE */}
          <div className="bg-[#f8fbff] rounded-3xl p-6 border border-[#edf3ff]">
            <label className="block text-[#0b1c39] font-bold mb-3">
              Current Age
            </label>

            <input
              type="range"
              min="18"
              max="70"
              value={currentAge}
              onChange={(e) =>
                setCurrentAge(Number(e.target.value))
              }
              className="w-full h-2 bg-[#dce7ff] rounded-lg appearance-none cursor-pointer"
            />

            <div className="text-center mt-3 font-bold text-[#2e56b8] text-xl">
              {currentAge} Years
            </div>
          </div>

          {/* RETIREMENT AGE */}
          <div className="bg-[#f8fbff] rounded-3xl p-6 border border-[#edf3ff]">
            <label className="block text-[#0b1c39] font-bold mb-3">
              Retirement Age
            </label>

            <input
              type="range"
              min="40"
              max="80"
              value={retirementAge}
              onChange={(e) =>
                setRetirementAge(Number(e.target.value))
              }
              className="w-full h-2 bg-[#dce7ff] rounded-lg appearance-none cursor-pointer"
            />

            <div className="text-center mt-3 font-bold text-[#2e56b8] text-xl">
              {retirementAge} Years
            </div>
          </div>

          {/* LIFE EXPECTANCY */}
          <div className="bg-[#f8fbff] rounded-3xl p-6 border border-[#edf3ff]">
            <label className="block text-[#0b1c39] font-bold mb-3">
              Life Expectancy
            </label>

            <input
              type="range"
              min="65"
              max="100"
              value={lifeExpectancy}
              onChange={(e) =>
                setLifeExpectancy(Number(e.target.value))
              }
              className="w-full h-2 bg-[#dce7ff] rounded-lg appearance-none cursor-pointer"
            />

            <div className="text-center mt-3 font-bold text-[#2e56b8] text-xl">
              {lifeExpectancy} Years
            </div>
          </div>

          {/* CURRENT SAVINGS */}
          <div className="bg-[#f8fbff] rounded-3xl p-6 border border-[#edf3ff]">
            <label className="block text-[#0b1c39] font-bold mb-3">
              Current Savings (₹)
            </label>

            <input
              type="number"
              value={currentSavings}
              onChange={(e) =>
                setCurrentSavings(Number(e.target.value))
              }
              className="w-full border border-[#dce7ff] rounded-2xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#2e56b8]"
            />
          </div>

          {/* SIP */}
          <div className="bg-[#f8fbff] rounded-3xl p-6 border border-[#edf3ff]">
            <label className="block text-[#0b1c39] font-bold mb-3">
              Monthly Investment SIP (₹)
            </label>

            <input
              type="number"
              value={monthlyInvestment}
              onChange={(e) =>
                setMonthlyInvestment(Number(e.target.value))
              }
              className="w-full border border-[#dce7ff] rounded-2xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#2e56b8]"
            />
          </div>

          {/* RETURN */}
          <div className="bg-[#f8fbff] rounded-3xl p-6 border border-[#edf3ff]">
            <label className="block text-[#0b1c39] font-bold mb-3">
              Expected Return (% p.a.)
            </label>

            <input
              type="range"
              min="1"
              max="20"
              step="0.5"
              value={expectedReturn}
              onChange={(e) =>
                setExpectedReturn(Number(e.target.value))
              }
              className="w-full h-2 bg-[#dce7ff] rounded-lg appearance-none cursor-pointer"
            />

            <div className="text-center mt-3 font-bold text-[#2e56b8] text-xl">
              {expectedReturn}%
            </div>
          </div>

          {/* MONTHLY EXPENSE */}
          <div className="bg-[#f8fbff] rounded-3xl p-6 border border-[#edf3ff]">
            <label className="block text-[#0b1c39] font-bold mb-3">
              Current Monthly Expense (₹)
            </label>

            <input
              type="number"
              value={monthlyExpense}
              onChange={(e) =>
                setMonthlyExpense(Number(e.target.value))
              }
              className="w-full border border-[#dce7ff] rounded-2xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#2e56b8]"
            />
          </div>

          {/* INFLATION */}
          <div className="bg-[#f8fbff] rounded-3xl p-6 border border-[#edf3ff]">
            <label className="block text-[#0b1c39] font-bold mb-3">
              Inflation Rate (% p.a.)
            </label>

            <input
              type="range"
              min="1"
              max="12"
              step="0.5"
              value={inflationRate}
              onChange={(e) =>
                setInflationRate(Number(e.target.value))
              }
              className="w-full h-2 bg-[#dce7ff] rounded-lg appearance-none cursor-pointer"
            />

            <div className="text-center mt-3 font-bold text-[#69bf74] text-xl">
              {inflationRate}%
            </div>
          </div>

          <button
            onClick={calculate}
            className="w-full bg-[#2e56b8] hover:bg-[#24479b] text-white py-5 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-lg"
          >
            Calculate Retirement Plan
          </button>
        </div>

        {/* RESULT */}
        {result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gradient-to-br from-[#edf3ff] to-[#eef8f1] rounded-[35px] p-8 border border-[#dce7ff]"
          >
            <h4 className="text-3xl font-bold text-[#0b1c39] mb-8">
              Retirement Summary
            </h4>

            <div className="space-y-5">
              <div className="bg-white rounded-2xl p-5 flex justify-between items-center shadow-sm">
                <span className="text-gray-600 font-medium">
                  Years to Retirement
                </span>

                <span className="font-bold text-[#0b1c39] text-xl">
                  {result.yearsToRetirement} Years
                </span>
              </div>

              <div className="bg-white rounded-2xl p-5 flex justify-between items-center shadow-sm">
                <span className="text-gray-600 font-medium">
                  Retirement Corpus
                </span>

                <span className="font-bold text-[#2e56b8] text-xl">
                  ₹{Math.round(result.totalCorpus).toLocaleString()}
                </span>
              </div>

              <div className="bg-white rounded-2xl p-5 flex justify-between items-center shadow-sm">
                <span className="text-gray-600 font-medium">
                  Required Corpus
                </span>

                <span className="font-bold text-[#0b1c39] text-xl">
                  ₹{Math.round(result.requiredCorpus).toLocaleString()}
                </span>
              </div>

              <div className="bg-white rounded-2xl p-5 flex justify-between items-center shadow-sm">
                <span className="text-gray-600 font-medium">
                  Inflation Adjusted Expense
                </span>

                <span className="font-bold text-[#69bf74] text-xl">
                  ₹{Math.round(result.inflationAdjustedExpense).toLocaleString()}
                </span>
              </div>

              <div className="bg-white rounded-2xl p-5 flex justify-between items-center shadow-sm">
                <span className="text-gray-600 font-medium">
                  Future Value of Savings
                </span>

                <span className="font-bold text-[#0b1c39] text-xl">
                  ₹{Math.round(result.futureValueOfSavings).toLocaleString()}
                </span>
              </div>

              <div className="bg-white rounded-2xl p-5 flex justify-between items-center shadow-sm">
                <span className="text-gray-600 font-medium">
                  Future Value of SIP
                </span>

                <span className="font-bold text-[#0b1c39] text-xl">
                  ₹{Math.round(result.futureValueOfSIP).toLocaleString()}
                </span>
              </div>

              {/* STATUS */}
              <div
                className={`rounded-3xl p-6 mt-6 ${result.isSufficient
                    ? 'bg-green-100 border border-green-300'
                    : 'bg-red-100 border border-red-300'
                  }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <FaShieldAlt />

                  <h5 className="font-bold text-lg">
                    Retirement Status
                  </h5>
                </div>

                {result.isSufficient ? (
                  <p className="text-green-700 font-semibold text-lg">
                    ✅ Excellent! Your retirement planning is on track.
                  </p>
                ) : (
                  <div>
                    <p className="text-red-700 font-semibold text-lg">
                      ⚠ You need additional retirement savings.
                    </p>

                    <div className="mt-4 bg-white rounded-2xl p-4">
                      <p className="text-gray-600 mb-2">
                        Additional SIP Required:
                      </p>

                      <h3 className="text-3xl font-bold text-red-600">
                        ₹{result.additionalMonthlyInvestment.toLocaleString()}/month
                      </h3>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* DOWNLOAD BUTTON */}
            <button
              onClick={() => setShowForm(true)}
              className="w-full mt-8 bg-[#69bf74] hover:bg-[#58ab63] text-white py-4 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-3"
            >
              <FaFilePdf />
              Download Professional PDF Report
            </button>
          </motion.div>
        )}
      </div>

      {/* PDF CONTENT */}
      {result && (
        <div
          id="retirement-result-content"
          style={{ display: 'none' }}
        >
          <div
            style={{
              padding: '30px',
              fontFamily: 'Arial',
              color: '#222',
            }}
          >
            {/* HEADER */}
            <div
              style={{
                borderBottom: '3px solid #2e56b8',
                paddingBottom: '20px',
                marginBottom: '20px',
                textAlign: 'center',
              }}
            >
              <h1 style={{ color: '#2e56b8' }}>
                Wisdom Consultancy
              </h1>

              <p>
                E-44, Vasant Vihar Society,
                GIDC-Vadsar Road,
                Vadodara-390010
              </p>

              <p>
                📞 +91 9726344477 |
                ✉ wisdom2secure@gmail.com
              </p>

              <h2 style={{ marginTop: '20px' }}>
                Retirement Planning Report
              </h2>
            </div>

            {/* TABLE */}
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <tbody>
                {[
                  ['Current Age', `${currentAge} Years`],
                  ['Retirement Age', `${retirementAge} Years`],
                  ['Life Expectancy', `${lifeExpectancy} Years`],
                  ['Current Savings', `₹${currentSavings.toLocaleString()}`],
                  ['Monthly SIP', `₹${monthlyInvestment.toLocaleString()}`],
                  ['Expected Return', `${expectedReturn}%`],
                  ['Inflation Rate', `${inflationRate}%`],
                  ['Current Monthly Expense', `₹${monthlyExpense.toLocaleString()}`],
                  ['Retirement Corpus', `₹${Math.round(result.totalCorpus).toLocaleString()}`],
                  ['Required Corpus', `₹${Math.round(result.requiredCorpus).toLocaleString()}`],
                ].map((row, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        padding: '12px',
                        border: '1px solid #ddd',
                        fontWeight: 'bold',
                      }}
                    >
                      {row[0]}
                    </td>

                    <td
                      style={{
                        padding: '12px',
                        border: '1px solid #ddd',
                      }}
                    >
                      {row[1]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* DISCLAIMER */}
            <div
              style={{
                marginTop: '30px',
                background: '#f5f5f5',
                padding: '20px',
                borderRadius: '10px',
              }}
            >
              <h3>
                Mutual Fund Disclaimer
              </h3>

              <p
                style={{
                  fontSize: '13px',
                  lineHeight: '24px',
                  color: '#555',
                }}
              >
                Mutual Fund investments are subject to market risks. Please read all scheme related documents carefully before investing. Past performance is not indicative of future returns.
              </p>

              <p style={{ marginTop: '10px' }}>
                ARN No: ARN-XXXXX
              </p>
            </div>

            {/* FOOTER */}
            <div
              style={{
                marginTop: '25px',
                textAlign: 'center',
                fontSize: '12px',
                color: '#777',
              }}
            >
              <p>
                This is a computer generated report by Wisdom Consultancy.
              </p>

              <p>
                www.wisdomconsultancy.in
              </p>
            </div>
          </div>
        </div>
      )}

      {/* FORM */}
      {showForm && (
        <CalculatorForm
          onSubmit={handleFormSubmit}
          onClose={() => setShowForm(false)}
          calculatorType="Retirement"
          calculationData={result}
          userInputs={{
            currentAge,
            retirementAge,
            lifeExpectancy,
            currentSavings,
            monthlyInvestment,
            expectedReturn,
            monthlyExpense,
            inflationRate,
          }}
        />
      )}
    </motion.div>
  );
};

export default RetirementCalculator;
